import React from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Job } from '@core/generated/gql-types';
import type { JobDrawerView } from '@/modules/jobs/views/jobDrawer.view';
import { useJobDrawerData } from '@/modules/jobs/hooks/useJobDrawerData';
import TimesheetsToActionSection from './jobDrawer/timesheetsSection/TimesheetsToActionSection';
import RateSection from './jobDrawer/rateSection/RateSection';
import DetailsSection from './jobDrawer/DetailsSection';
import HeaderSection from './jobDrawer/HeaderSection';
import WorkerSection from './jobDrawer/workerSection/WorkerSection';

interface JobDrawerProps {
  job: Job | null;
  isOpen: boolean;
  onClose: () => void;
  view: JobDrawerView;
}

const JobDrawer = ({ job, isOpen, onClose, view }: JobDrawerProps) => {
  useJobDrawerData(view, job, isOpen);

  if (!job) {
    return null;
  }

  const showWorkerSection = view.sections.worker !== 'none';

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => {
          onClose();
        }}
      >
        <Transition.Child
          as={React.Fragment}
          enter="ease-in-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/5" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={React.Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-lg">
                  <div className="flex h-full flex-col overflow-y-scroll shadow-xl">
                    <HeaderSection job={job} onClose={onClose} />

                    <div className="flex-1 space-y-6 bg-gray-100 px-6 py-6">
                      {view.sections.details && (
                        <DetailsSection
                          job={job}
                          showClientName={view.actions.showClientName}
                        />
                      )}

                      {view.sections.rate && <RateSection job={job} />}

                      {showWorkerSection && (
                        <WorkerSection
                          job={job}
                          mode={view.sections.worker}
                          allowUnassignWorker={view.actions.allowUnassignWorker}
                          showWorkerPayRate={view.actions.showWorkerPayRate}
                        />
                      )}

                      {view.sections.timesheets && (
                        <TimesheetsToActionSection job={job} />
                      )}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default JobDrawer;
